import React, { useEffect, useState } from "react";
import { Seo } from "@/components/Seo";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { VOLUNTEER } from "@/constants/testIds";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const nav = useNavigate();
  const { user: sessionUser, setUser, logout } = useAuth();
  const [mode, setMode] = useState("signin"); // signin | register | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [realName, setRealName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [info, setInfo] = useState("");
  const [devEmail, setDevEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [devEnabled, setDevEnabled] = useState(false);

  useEffect(() => {
    // Pods hibernate between sessions and the first fetch after wake can hit
    // the backend's 502 boot window. Brief retry (3 attempts, 2.5s/5s backoff
    // — same spirit as the dev-session auto-retry) so the demo section
    // appears once the backend is up, without a manual reload.
    let cancelled = false;
    const attempt = (n) => {
      api.authConfig()
        .then((c) => { if (!cancelled) setDevEnabled(!!c.dev_login); })
        .catch(() => {
          if (cancelled) return;
          if (n < 2) setTimeout(() => attempt(n + 1), 2500 * (n + 1));
          else setDevEnabled(false);
        });
    };
    attempt(0);
    return () => { cancelled = true; };
  }, []);

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const finishAuth = (data, welcome = true) => {
    if (data.session_token) localStorage.setItem("session_token", data.session_token);
    setUser(data.user);
    if (welcome) toast.success(`Welcome, ${data.user.name}`);
    if (data.admin_otp_required) {
      toast.info("Admin verification: we emailed you a 6-digit code — enter it on the Admin page.");
    }
    // 17-C item 6: logged-out "Join" on a collective round-trips through
    // login — onboarded users go straight back to complete the join
    // (CollectiveDetail consumes the flag and auto-joins). New users keep the
    // flag through onboarding; the completion screen routes them back.
    let joinId = null;
    try { joinId = sessionStorage.getItem("civic_return_join"); } catch { /* ignore */ }
    if (data.user.onboarded && joinId) return nav(`/collectives/${joinId}`);
    nav(data.user.onboarded ? "/dashboard" : "/onboarding", { state: { user: data.user } });
  };

  const submitSignin = async (e) => {
    e?.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    setInfo("");
    try {
      finishAuth(await api.login(email.trim(), password));
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) toast.error(detail || "Too many attempts — wait a few minutes and try again.");
      else if (status === 401) toast.error("Invalid email or password.");
      else toast.error(detail || "Sign in failed — please try again.");
    } finally { setLoading(false); }
  };

  const submitRegister = async (e) => {
    e?.preventDefault();
    if (!realName || !email || !password) return;
    setLoading(true);
    setInfo("");
    try {
      const data = await api.register({
        real_name: realName.trim(),
        display_name: displayName.trim() || undefined, // backend defaults to real name
        email: email.trim(), password,
      });
      if (data.session_token) {
        finishAuth(data);
      } else {
        // Anti-enumeration path: same generic message whether or not the email existed.
        setInfo(data.message || "Almost done — check your inbox for the next step.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create your account — please try again.");
    } finally { setLoading(false); }
  };

  const submitForgot = async (e) => {
    e?.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const data = await api.resetRequest(email.trim());
      setInfo(data.message || "If an account exists for that email, we've sent a password reset link.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong — please try again.");
    } finally { setLoading(false); }
  };

  const switchMode = (m) => { setMode(m); setInfo(""); setPassword(""); };

  const handleDev = async (emailArg) => {
    const em = typeof emailArg === "string" ? emailArg : devEmail;
    if (!em) return toast.error("Enter a demo email");
    setLoading(true);
    try {
      let data;
      try {
        data = await api.devSession(em);
      } catch (e) {
        // Preview pods hibernate: a first click can hit a waking backend and
        // get a transient 502. Wait a beat and retry once before giving up.
        await new Promise(r => setTimeout(r, 2000));
        data = await api.devSession(em);
      }
      finishAuth(data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429) toast.error("Too many login attempts — wait a minute and try again.");
      else if (status === 403) toast.error(detail || "Demo login is disabled on this server.");
      else if (status) toast.error(detail || `Demo login failed (HTTP ${status}). Please try again.`);
      else toast.error("Server unreachable — it may be waking up. Try again in a few seconds.");
    } finally { setLoading(false); }
  };

  const quickLogin = async (em) => {
    try { await api.seedDemo(); } catch {}
    setDevEmail(em);
    await handleDev(em);
  };

  // Demo personas — rendered ONLY when the backend reports dev_login enabled
  // (GET /api/auth/config). That flag is hard-guarded server-side by
  // core._dev_login_allowed(): it is false unless ALLOW_DEV_LOGIN=true AND the
  // APP_URL host is a *.preview.emergentagent.com / localhost domain, so this
  // section can never render against production.
  const DEMO_ACCOUNTS = [
    ["alex.volunteer@demo.civic", "Volunteer", "Alex Rivera — onboarded, London"],
    ["sam.helper@demo.civic", "Volunteer 2", "Sam Patel — onboarded, Manchester"],
    ["greenfields@demo.civic", "Org · verified", "GreenFields Trust"],
    ["riverbank@demo.civic", "Org · pending", "Riverbank Arts CIC — awaiting review"],
    ["admin@demo.civic", "Admin", "Platform admin console"],
  ];

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6 py-16">
      <Seo title="Sign in" />
      <Card className="w-full max-w-md border-neutral-200">
        <CardContent className="p-10">
          {/* Item 7 (2026-07): signed-in awareness — /login is reachable while
              authenticated (e.g. testing demo personas). Make the current
              session obvious; persona buttons still replace it cleanly. */}
          {sessionUser && (
            <div data-testid="login-signed-in-banner" className="mb-6 rounded-xl border border-forest/20 bg-forest/5 px-4 py-3">
              <div className="text-sm text-forest">
                You're signed in as <b data-testid="login-signed-in-name">{sessionUser.name}</b>.
              </div>
              <div className="flex gap-2 mt-2.5">
                <Button data-testid="login-goto-dashboard-btn" size="sm" onClick={() => nav("/dashboard")} className="rounded-full h-8 px-4 text-xs bg-forest hover:bg-forest-hover">
                  Go to dashboard
                </Button>
                <Button data-testid="login-signout-btn" size="sm" variant="outline" onClick={() => logout()} className="rounded-full h-8 px-4 text-xs">
                  Sign out
                </Button>
              </div>
            </div>
          )}
          <div className="font-display text-3xl text-forest tracking-tight">
            {mode === "register" ? "Join CivicHearth" : mode === "forgot" ? "Reset your password" : "Welcome back"}
          </div>
          <p className="text-neutral-600 mt-2 text-sm">
            {mode === "register"
              ? "Create a free account to volunteer, post opportunities and track your impact."
              : mode === "forgot"
                ? "Enter your email and we'll send you a single-use reset link (valid for 1 hour)."
                : "Sign in to volunteer, post opportunities and track your impact."}
          </p>

          {info && (
            <div data-testid="auth-info-message" className="mt-5 rounded-xl border border-forest/20 bg-forest/5 px-4 py-3 text-sm text-forest">
              {info}
            </div>
          )}

          {mode !== "forgot" && (
            <>
              <Button
                data-testid={VOLUNTEER.loginGoogle}
                onClick={handleGoogle}
                className="w-full mt-6 h-12 rounded-full bg-forest hover:bg-forest-hover text-white"
              >
                Continue with Google
              </Button>
              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-neutral-200" />
                <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-400">or with email</span>
                <div className="flex-1 h-px bg-neutral-200" />
              </div>
            </>
          )}

          {mode === "signin" && (
            <form onSubmit={submitSignin} className="space-y-3">
              <Input
                data-testid="login-email-input"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11"
              />
              <Input
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11"
              />
              <Button
                data-testid="login-submit-btn"
                type="submit"
                disabled={loading || !email || !password}
                className="w-full h-11 rounded-full bg-forest hover:bg-forest-hover text-white"
              >
                {loading ? "Signing in…" : "Sign in"}
              </Button>
              <div className="flex items-center justify-between text-xs pt-1">
                <button type="button" data-testid="login-forgot-link" onClick={() => switchMode("forgot")} className="text-neutral-500 hover:text-forest underline underline-offset-2">
                  Forgot password?
                </button>
                <button type="button" data-testid="login-goto-register" onClick={() => switchMode("register")} className="text-forest font-semibold hover:underline underline-offset-2">
                  New here? Create an account
                </button>
              </div>
            </form>
          )}

          {mode === "register" && (
            <form onSubmit={submitRegister} className="space-y-3">
              <div>
                <Input
                  data-testid="register-name-input"
                  autoComplete="name"
                  placeholder="Your full name"
                  value={realName}
                  onChange={(e) => setRealName(e.target.value)}
                  className="h-11"
                />
                <p className="text-[11px] text-neutral-400 mt-1">Kept private — never shown to other members.</p>
              </div>
              <div>
                <Input
                  data-testid="register-display-name-input"
                  autoComplete="nickname"
                  placeholder="Display name (optional)"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="h-11"
                />
                <p className="text-[11px] text-neutral-400 mt-1">What others see across CivicHearth — defaults to your full name.</p>
              </div>
              <Input
                data-testid="register-email-input"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11"
              />
              <Input
                data-testid="register-password-input"
                type="password"
                autoComplete="new-password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11"
              />
              <p className="text-[11px] text-neutral-400">At least 8 characters, with a letter and a number.</p>
              <Button
                data-testid="register-submit-btn"
                type="submit"
                disabled={loading || !realName || !email || !password}
                className="w-full h-11 rounded-full bg-forest hover:bg-forest-hover text-white"
              >
                {loading ? "Creating account…" : "Create account"}
              </Button>
              <div className="text-center text-xs pt-1">
                <button type="button" data-testid="register-goto-login" onClick={() => switchMode("signin")} className="text-forest font-semibold hover:underline underline-offset-2">
                  Already have an account? Sign in
                </button>
              </div>
            </form>
          )}

          {mode === "forgot" && (
            <form onSubmit={submitForgot} className="space-y-3 mt-6">
              <Input
                data-testid="forgot-email-input"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11"
              />
              <Button
                data-testid="forgot-submit-btn"
                type="submit"
                disabled={loading || !email}
                className="w-full h-11 rounded-full bg-forest hover:bg-forest-hover text-white"
              >
                {loading ? "Sending…" : "Email me a reset link"}
              </Button>
              <div className="text-center text-xs pt-1">
                <button type="button" data-testid="forgot-back-to-login" onClick={() => switchMode("signin")} className="text-forest font-semibold hover:underline underline-offset-2">
                  Back to sign in
                </button>
              </div>
            </form>
          )}

          {devEnabled && mode === "signin" && (
            <>
              <div className="flex items-center gap-3 my-6">
                <div className="flex-1 h-px bg-neutral-200" />
                <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-400">Demo mode</span>
                <div className="flex-1 h-px bg-neutral-200" />
              </div>

              <div className="space-y-2">
                <Input
                  data-testid="login-dev-email"
                  placeholder="alex.volunteer@demo.civic"
                  value={devEmail}
                  onChange={(e) => setDevEmail(e.target.value)}
                  className="h-11"
                />
                <Button
                  data-testid={VOLUNTEER.loginDev}
                  onClick={() => handleDev()}
                  disabled={loading}
                  variant="outline"
                  className="w-full h-11 rounded-full border-forest/20 text-forest hover:bg-forest/5"
                >
                  {loading ? "Loading…" : "Sign in with demo email"}
                </Button>
                <div data-testid="demo-accounts-section" className="mt-4 rounded-xl border border-dashed border-amber-300 bg-amber-50/60 p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700">Demo accounts</span>
                    <span className="text-[10px] text-amber-600 italic">For testing only — not shown in production</span>
                  </div>
                  <div className="mt-3 space-y-1.5">
                    {DEMO_ACCOUNTS.map(([em, label, hint]) => (
                      <button
                        key={em}
                        data-testid={`demo-login-${em.split("@")[0].replace(/\./g, "-")}`}
                        aria-label={`Sign in as ${hint.split(" — ")[0]}, ${label} demo account`}
                        disabled={loading}
                        onClick={() => quickLogin(em)}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-white/70 border border-amber-200/70 hover:border-forest/40 hover:bg-white text-left transition-colors disabled:opacity-50"
                      >
                        <span>
                          <span className="text-xs font-semibold text-forest">{label}</span>
                          <span className="block text-[11px] text-neutral-500">{hint}</span>
                        </span>
                        <span className="text-[10px] font-mono text-neutral-400 shrink-0">{em}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
